import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import styles from './UserForm.module.css';
import Input from '../../common/Input/Input';
import Button from '../../common/Button/Button';
import Select from '../../common/Select/Select';
import { validateEmail, validatePhone } from '../../../utils/validation';
import { VALIDATION_MESSAGES } from '../../../utils/constants';
import { preferenceService } from '../../../services/preferenceService';
import { termTypesService } from '../../../services/termsService';
import { formatFileSize } from '../../../utils/fileUtils';
import api from '../../../services/api';

const toRoleValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value.trim();
  return '';
};

const parseRoleInput = (rawValue) => {
  if (!rawValue) return '';

  if (Array.isArray(rawValue)) {
    return parseRoleInput(rawValue[0]);
  }

  if (typeof rawValue === 'object') {
    return (
      toRoleValue(rawValue.id) ||
      toRoleValue(rawValue.role_id) ||
      toRoleValue(rawValue.access_role_id) ||
      toRoleValue(rawValue.roleType) ||
      toRoleValue(rawValue.role_type) ||
      toRoleValue(rawValue.name)
    );
  }

  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim();
    if (!trimmed) return '';

    try {
      const parsed = JSON.parse(trimmed);
      return parseRoleInput(parsed);
    } catch {
      return trimmed;
    }
  }

  return toRoleValue(rawValue);
};

const resolvePermissionValue = (user, roles = []) => {
  const directCandidate = parseRoleInput(
    user?.permissions ??
      user?.access_roles ??
      user?.access_role ??
      user?.role_id ??
      user?.roleid ??
      user?.role
  );

  if (!directCandidate) return '';

  if (roles.length === 0) return directCandidate;

  const exactIdMatch = roles.find((role) => String(role.id) === directCandidate);
  if (exactIdMatch) return String(exactIdMatch.id);

  const normalized = directCandidate.toLowerCase();
  const nameMatch = roles.find((role) => {
    const roleType = String(role.role_type || '').toLowerCase();
    const roleName = String(role.name || '').toLowerCase();
    return roleType === normalized || roleName === normalized;
  });

  return nameMatch ? String(nameMatch.id) : '';
};


const UserForm = ({ user, onSubmit, onCancel, loading, fullscreenMode = false, onFormDataChange }) => {
  const [isEditing, setIsEditing] = useState(!!user);
  const [roles, setRoles] = useState([])
  const [preferences, setPreferences] = useState({
    genders: [],
    prefStocks: [],
    appPreferences: [],
    timelineContent: [],
  });

  const [termTypes, setTermTypes] = useState({
    termTypes: []
  });

  const [preferencesLoading, setPreferencesLoading] = useState(true);


  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
    setValue,
    control,
  } = useForm({
    defaultValues: {
      name: user?.name || '',
      name: user?.name || '',
      username: user?.username || '',
      userimage: user?.userimage || '',
      password: '',
      email: user?.email || '',
      phone: user?.phone || '',
      signupip: user?.signupip || '',
      signupdeviceid: user?.signupdeviceid || '',
      isresearchanalyst: String(user?.isresearchanalyst || 0),
      ranumber: user?.ranumber || '',
      ravalidity: user?.ravalidity || '',
      isblocked: String(user?.isblocked || 0),
      blockingnote: user?.blockingnote || '',
      agreement: user?.agreement || [],
      documents: user?.documents || [],
      age: user?.age || '',
      gender: user?.gender || '',
      prefsectors: user?.prefsectors || [],
      prefindustries: user?.prefindustries || [],
      prefstocks: user?.prefstocks || [],
      needguidance: user?.needguidance || '',
      experienceinmarket: user?.experienceinmarket || '',
      preferenceonapp: user?.preferenceonapp || [],
      timelinecontent: user?.timelinecontent || [],
      permissions: resolvePermissionValue(user),
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'agreement',
  });

  const { fields: documentFields, append: appendDocument, remove: removeDocument } = useFieldArray({
    control,
    name: 'documents',
  });

  const watchIsResearchAnalyst = watch('isresearchanalyst');
  const watchName = watch('name');
  const watchEmail = watch('email');
  const watchPhone = watch('phone');
  const watchedFields = watch();

  const shouldShowPreferences = watchName?.trim() && watchEmail?.trim() && watchPhone?.trim();



  useEffect(() => {
    if (onFormDataChange) {
      onFormDataChange({
        name: watchName || '',
        username: watchedFields.username || '',
        email: watchEmail || '',
        phone: watchPhone || ''
      });
    }
  }, [watchName, watchEmail, watchPhone, onFormDataChange]);

  const loadPreferences = async () => {
    try {
      setPreferencesLoading(true);
      const [gendersResponse, prefStocksResponse, appPreferencesResponse, timelineContentResponse] = await Promise.all([
        preferenceService.getAllGenders(),
        preferenceService.getAllPrefStocks(),
        preferenceService.getAllPreferences(),
        preferenceService.getAllTimelineContent(),
      ]);

      const extractData = (response) => {
        if (response && typeof response === 'object') {
          return response.data || response;
        }
        return response || [];
      };

      setPreferences({
        genders: extractData(gendersResponse),
        prefStocks: extractData(prefStocksResponse),
        appPreferences: extractData(appPreferencesResponse),
        timelineContent: extractData(timelineContentResponse),
      });
    } catch (error) {
      console.error('Error loading preferences:', error);
      setPreferences({
        genders: [],
        prefStocks: [],
        appPreferences: [],
        timelineContent: [],
      });
    } finally {
      setPreferencesLoading(false);
    }
  };

  const loadTermTypes = async () => {
    try {
      const [termTypesResponse] = await Promise.all([
        termTypesService.getAllTermTypes()
      ]);

      const extractData = (response) => {
        if (response && typeof response === 'object') {
          return response.data || response;
        }
        return response || [];
      };

      setTermTypes({
        termTypes: extractData(termTypesResponse)
      });

    } catch (error) {
      setTermTypes({
        termTypes: [],
      });
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await api.get(`/access-roles`)
      if (res.data) {
        setRoles(res.data)
      }
    } catch (error) {
      console.error(error?.message || 'Error fetching roles')
    }

  }

  useEffect(() => {
    loadTermTypes();
    loadPreferences();
    fetchRoles()
  }, []);

  useEffect(() => {
    if (user) {
      const parseArrayField = (field) => {
        if (!field) return [];
        if (Array.isArray(field)) return field;
        if (typeof field === 'string') {
          try {
            const parsed = JSON.parse(field);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return field.split(',').map(s => s.trim()).filter(Boolean);
          }
        }
        return [];
      };

      const parseagreement = (agreement) => {
        if (!agreement) return [];
        if (Array.isArray(agreement)) return agreement;
        if (typeof agreement === 'string') {
          try {
            const parsed = JSON.parse(agreement);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        }
        return [];
      };

      reset({
        name: user.name || '',
        username: user.username || '',
        userimage: user.userimage || '',
        password: '',
        email: user.email || '',
        phone: user.phone || '',
        signupip: user.signupip || '',
        signupdeviceid: user.signupdeviceid || '',
        isresearchanalyst: String(user.isresearchanalyst || 0),
        ranumber: user.ranumber || '',
        ravalidity: user.ravalidity || '',
        isblocked: String(user.isblocked || 0),
        blockingnote: user.blockingnote || '',
        agreement: parseagreement(user.agreement),
        documents: parseagreement(user.documents),
        age: user.age || '',
        gender: user.gender || '',
        prefsectors: parseArrayField(user.prefsectors),
        prefindustries: parseArrayField(user.prefindustries),
        prefstocks: parseArrayField(user.prefstocks),
        needguidance: user.needguidance || '',
        experienceinmarket: user.experienceinmarket || '',
        preferenceonapp: parseArrayField(user.preferenceonapp),
        timelinecontent: parseArrayField(user.timelinecontent),
        permissions: resolvePermissionValue(user),
      });
      setIsEditing(true);
    }
  }, [user, reset]);

  useEffect(() => {
    if (!user || roles.length === 0) return;
    setValue('permissions', resolvePermissionValue(user, roles), { shouldDirty: false });
  }, [user, roles, setValue]);

  const handleFileChange = (event, index) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      event.target.value = '';
      return;
    }

    setValue(`agreement.${index}.agreementDocument`, file, { shouldDirty: true });
    setValue(`agreement.${index}.fileName`, file.name, { shouldDirty: true });
    setValue(`agreement.${index}.fileType`, file.type, { shouldDirty: true });
    setValue(`agreement.${index}.fileSize`, file.size, { shouldDirty: true });

  };

  const handleDocumentFileChange = (event, index) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      event.target.value = '';
      return;
    }

    setValue(`documents.${index}.documentFile`, file, { shouldDirty: true });
    setValue(`documents.${index}.fileName`, file.name, { shouldDirty: true });
    setValue(`documents.${index}.fileType`, file.type, { shouldDirty: true });
    setValue(`documents.${index}.fileSize`, file.size, { shouldDirty: true });

  };

  const removeFile = (index) => {
    setValue(`agreement.${index}.agreementDocument`, '', { shouldDirty: true });
    setValue(`agreement.${index}.fileName`, '', { shouldDirty: true });
    setValue(`agreement.${index}.fileType`, '', { shouldDirty: true });
    setValue(`agreement.${index}.fileSize`, '', { shouldDirty: true });

    const fileInput = document.getElementById(`file-input-${index}`);
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const removeDocumentFile = (index) => {
    setValue(`documents.${index}.documentFile`, '', { shouldDirty: true });
    setValue(`documents.${index}.fileName`, '', { shouldDirty: true });
    setValue(`documents.${index}.fileType`, '', { shouldDirty: true });
    setValue(`documents.${index}.fileSize`, '', { shouldDirty: true });

    const fileInput = document.getElementById(`document-file-input-${index}`);
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const onFormSubmit = (data) => {
    if (onSubmit) {
      const formattedData = {
        ...data,
        isresearchanalyst: parseInt(data.isresearchanalyst) || 0,
        isblocked: parseInt(data.isblocked) || 0,
        permissions: data.permissions ? parseInt(data.permissions) : 1,
        ...(isEditing && { userid: user.userid }),
      };

      if (shouldShowPreferences) {
        if (data.age && data.age !== '') {
          formattedData.age = parseInt(data.age);
        } else {
          formattedData.age = null;
        }

        formattedData.prefsectors = Array.isArray(data.prefsectors)
          ? JSON.stringify(data.prefsectors)
          : data.prefsectors;
        formattedData.prefindustries = Array.isArray(data.prefindustries)
          ? JSON.stringify(data.prefindustries)
          : data.prefindustries;
        formattedData.prefstocks = Array.isArray(data.prefstocks)
          ? JSON.stringify(data.prefstocks)
          : data.prefstocks;
        formattedData.preferenceonapp = Array.isArray(data.preferenceonapp)
          ? JSON.stringify(data.preferenceonapp)
          : data.preferenceonapp;
        formattedData.timelinecontent = Array.isArray(data.timelinecontent)
          ? JSON.stringify(data.timelinecontent)
          : data.timelinecontent;
      }

      if (formattedData.isresearchanalyst === 0) {
        delete formattedData.ranumber;
        delete formattedData.ravalidity;
        delete formattedData.isblocked;
        delete formattedData.blockingnote;
        delete formattedData.agreement;
      }

      if (!shouldShowPreferences) {
        delete formattedData.age;
        delete formattedData.gender;
        delete formattedData.prefsectors;
        delete formattedData.prefindustries;
        delete formattedData.prefstocks;
        delete formattedData.needguidance;
        delete formattedData.experienceinmarket;
        delete formattedData.preferenceonapp;
        delete formattedData.timelinecontent;
      }

      onSubmit(formattedData);
    }
  };

  const handleReset = () => {
    if (user) {
      reset();
    } else {
      reset({
        name: '',
        username: '',
        password: '',
        email: '',
        phone: '',
        signupip: '',
        signupdeviceid: '',
        isresearchanalyst: '0',
        ranumber: '',
        ravalidity: '',
        isblocked: '0',
        blockingnote: '',
        agreement: [],
        documents: [],
        age: '',
        gender: '',
        prefsectors: [],
        prefindustries: [],
        prefstocks: [],
        needguidance: '',
        experienceinmarket: '',
        preferenceonapp: [],
        timelinecontent: [],
        permissions: '',
      });
    }
    fields.forEach((_, index) => {
      const fileInput = document.getElementById(`file-input-${index}`);
      if (fileInput) fileInput.value = '';
    });
    documentFields.forEach((_, index) => {
      const fileInput = document.getElementById(`document-file-input-${index}`);
      if (fileInput) fileInput.value = '';
    });
  };

  const addNewAgreement = () => {
    append({
      agreementId: null,
      agreementDate: new Date().toISOString().split('T')[0],
      agreementignDate: '',
      agreementtatus: 'pending',
      approvalDate: '',
      expiryDate: '',
      agreementDocument: '',
      fileName: '',
      fileType: '',
      fileSize: null,
      notes: '',
    });
  };

  const removeAgreement = (index) => {
    const agreement = fields[index];
    if (agreement.agreementId) {
      setValue(`agreement.${index}._deleted`, true);
    } else {
      remove(index);
    }
  };

  const addNewDocument = () => {
    appendDocument({
      documentNumber: '',
      documentType: '',
      uploadDate: new Date().toISOString().split('T')[0],
      verifiedDate: '',
      verifiedTime: '',
      verifiedType: '',
      verifiedBy: '',
      verifiedNote: '',
      documentFile: '',
      fileName: '',
      fileType: '',
      fileSize: null,
    });
  };

  const removeDocumentItem = (index) => {
    const document = documentFields[index];
    if (document.documentId) {
      setValue(`documents.${index}._deleted`, true);
    } else {
      removeDocument(index);
    }
  };

  const genderOptions = preferences.genders.map(gender => ({
    value: gender.genderid || gender.id,
    label: gender.gendername || gender.name
  }));

  const prefStockOptions = preferences.prefStocks.map(stock => ({
    value: stock.prefstockid || stock.id,
    label: stock.stockname || stock.name
  }));

  const appPreferenceOptions = preferences.appPreferences.map(pref => ({
    value: pref.preferenceid || pref.id,
    label: pref.preferencename || pref.name
  }));

  const timelineContentOptions = preferences.timelineContent.map(content => ({
    value: content.timelineid || content.id,
    label: content.contentname || content.name
  }));

  const termTypesOptions = termTypes.termTypes.map((types) => ({
    value: types.id,
    label: types.name,
  }));

  const experienceOptions = [
    { value: 'beginner', label: 'Beginner (0-1 years)' },
    { value: 'intermediate', label: 'Intermediate (1-3 years)' },
    { value: 'advanced', label: 'Advanced (3-5 years)' },
    { value: 'expert', label: 'Expert (5+ years)' }
  ];

  const needGuidanceOptions = [
    { value: 'yes', label: 'Yes, I need guidance' },
    { value: 'no', label: 'No, I am experienced' },
    { value: 'sometimes', label: 'Sometimes' }
  ];

  const agreementtatusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'signed', label: 'Signed' },
    { value: 'expired', label: 'Expired' },
    { value: 'revoked', label: 'Revoked' }
  ];

  const documentTypeOptions = [
    { value: 'PAN', label: 'PAN Card' },
    { value: 'AADHAAR', label: 'Aadhaar Card' },
    { value: 'PASSPORT', label: 'Passport' },
    { value: 'DRIVING_LICENSE', label: 'Driving License' },
    { value: 'VOTER_ID', label: 'Voter ID' },
    { value: 'GST', label: 'GST Certificate' },
    { value: 'INCORPORATION', label: 'Certificate of Incorporation' },
    { value: 'OTHER', label: 'Other' }
  ];

  const formClasses = [
    styles.form,
    fullscreenMode ? styles.fullscreenModal : ''
  ].filter(Boolean).join(' ');

  return (
    <div className={fullscreenMode ? 'fullscreen-form-container' : ''}>
      <form className={formClasses} onSubmit={handleSubmit(onFormSubmit)}>
        {!fullscreenMode && (
          <div className={styles.formHeader}>
            <h2 className={styles.formTitle}>
              {isEditing ? 'Edit User' : 'Create New User'}
            </h2>
            <p className={styles.formSubtitle}>
              {isEditing
                ? 'Update user information below'
                : 'Fill in the details to create a new user account'
              }
            </p>
          </div>
        )}

        <div className={styles.formGrid}>
          <div className={styles.sectionTitle}>Basic Information</div>
          {fullscreenMode && (
            <div className={styles.sectionSubtitle}>
              Essential user details and contact information
            </div>
          )}

          <div className={styles.formGroup}>
            <Input
              label="Full Name"
              placeholder="Enter full name"
              required
              error={errors.name?.message}
              {...register('name', {
                required: VALIDATION_MESSAGES.REQUIRED,
                minLength: {
                  value: 2,
                  message: VALIDATION_MESSAGES.MIN_LENGTH(2),
                },
                maxLength: {
                  value: 100,
                  message: VALIDATION_MESSAGES.MAX_LENGTH(100),
                },
              })}
            />
          </div>

          <div className={styles.formGroup}>
            <Input
              label="Username"
              placeholder="Enter username"
              error={errors.username?.message}
              {...register('username', {
                maxLength: {
                  value: 50,
                  message: VALIDATION_MESSAGES.MAX_LENGTH(50),
                },
              })}
            />
          </div>

          {!isEditing && (
            <div className={styles.formGroup}>
              <Input
                label="Password"
                type="password"
                placeholder="Enter password"
                required={!isEditing}
                error={errors.password?.message}
                {...register('password', {
                  required: !isEditing ? VALIDATION_MESSAGES.REQUIRED : false,
                  minLength: {
                    value: 6,
                    message: VALIDATION_MESSAGES.MIN_LENGTH(6),
                  },
                })}
              />
            </div>
          )}





          <div className={styles.formGroup}>
            <Input
              label="Email Address"
              type="email"
              placeholder="user@example.com"
              error={errors.email?.message}
              {...register('email', {
                validate: (value) => {
                  if (!value) return true;
                  return validateEmail(value) || VALIDATION_MESSAGES.INVALID_EMAIL;
                },
                maxLength: {
                  value: 255,
                  message: VALIDATION_MESSAGES.MAX_LENGTH(255),
                },
              })}
            />
          </div>

          <div className={styles.formGroup}>
            <Input
              label="Phone Number"
              type="tel"
              placeholder="+1234567890"
              required
              error={errors.phone?.message}
              {...register('phone', {
                required: VALIDATION_MESSAGES.REQUIRED,
                validate: (value) =>
                  validatePhone(value) || VALIDATION_MESSAGES.INVALID_PHONE,
                maxLength: {
                  value: 20,
                  message: VALIDATION_MESSAGES.MAX_LENGTH(20),
                },
              })}
            />
          </div>

          <div className={styles.formGroup}>
            <div className={styles.formGroup}>
              <Input
                label="Signup IP"
                placeholder="Enter IP address"
                readOnly={isEditing}
                error={errors.signupip?.message}
                {...register('signupip', {
                  maxLength: {
                    value: 100,
                    message: VALIDATION_MESSAGES.MAX_LENGTH(100),
                  },
                })}
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <Input
              label="Device ID"
              placeholder="Enter device identifier"
              readOnly={isEditing}
              error={errors.signupdeviceid?.message}
              {...register('signupdeviceid', {
                maxLength: {
                  value: 100,
                  message: VALIDATION_MESSAGES.MAX_LENGTH(100),
                },
              })}
            />
          </div>

          {/* {watchedFields.isresearchanalyst && (
            <>
              <div className={styles.formGroup}>
                <Input
                  label="RA Number"
                  placeholder="Enter RA Number"
                  error={errors.ranumber?.message}
                  {...register('ranumber', {
                    required: watchedFields.isresearchanalyst ? "RA Number is required" : false
                  })}
                />
              </div>

              <div className={styles.formGroup}>
                <Input
                  label="RA Validity"
                  type="date"
                  error={errors.ravalidity?.message}
                  {...register('ravalidity', {
                    required: watchedFields.isresearchanalyst ? "Validity date is required" : false
                  })}
                />
              </div>
            </>
          )} */}

          {fullscreenMode && (
            <div className={styles.sectionSubtitle}>
              Configure research analyst permissions and account status
            </div>
          )}

                    <div className={styles.formGroup}>
            <label className={styles.label}>Role</label>
          
            <select
              className={styles.select}
              name="access_roles"
              {...register('permissions')}
            >
              <option value="" >Access Roles</option>
              {roles.map((chapter) => (
                <option key={chapter.id} value={chapter.id}>
                  {chapter.role_type}
                </option>
              ))}
            </select>
            {errors.permissions && <span className={styles.error}>{errors.permissions.message}</span>}
          </div>

          {/* <div className={styles.formGrid}> */}
          <div className={styles.formGroup}>
            <label className={styles.radioGroupLabel}>Research Analyst</label>
            <div className={styles.radioOptions}>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  value="0"
                  className={styles.radio}
                  {...register('isresearchanalyst')}
                />
                <span className={styles.radioText}>No</span>
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  value="1"
                  className={styles.radio}
                  {...register('isresearchanalyst')}
                />
                <span className={styles.radioText}>Yes</span>
              </label>
            </div>
          </div>

          {/* </div> */}

          {watchIsResearchAnalyst === '1' && (
            <>
              <div className={styles.formGroup}>
                <Input
                  label="RA Number"
                  placeholder="Enter RA number"
                  required
                  error={errors.ranumber?.message}
                  {...register('ranumber', {
                    required: watchIsResearchAnalyst === '1' ? VALIDATION_MESSAGES.REQUIRED : false,
                    maxLength: {
                      value: 100,
                      message: VALIDATION_MESSAGES.MAX_LENGTH(100),
                    },
                  })}
                />
              </div>

              <div className={styles.formGroup}>
                <Input
                  label="RA Validity"
                  type="date"
                  required
                  error={errors.ravalidity?.message}
                  {...register('ravalidity', {
                    required: watchIsResearchAnalyst === '1' ? VALIDATION_MESSAGES.REQUIRED : false,
                  })}
                />
              </div>
            </>
          )}
        </div>



        {/* {termTypesOptions.map((termTypes) => {
          return (
            <div key={termTypes.value}>
              <label style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <input type="checkbox" value={termTypes.value} />
                <span>{termTypes.label}</span>
              </label>
            </div>
          );
        })} */}

        {
          preferencesLoading && shouldShowPreferences && (
            <div className={styles.loadingMessage}>
              <span>Loading preferences...</span>
            </div>
          )
        }

        {
          !fullscreenMode && (
            <div className={styles.formActions}>
              <Button
                type="button"
                variant="secondary"
                onClick={handleReset}
                disabled={loading}
              >
                Reset
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={loading}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                variant="primary"
                loading={loading}
                disabled={loading || (!isDirty && isEditing)}
              >
                {isEditing ? 'Update User' : 'Create User'}
              </Button>
            </div>
          )
        }

        {
          isEditing && !isDirty && !fullscreenMode && (
            <div className={styles.noChangesMessage}>
              <span>💡 Make changes to enable the update button</span>
            </div>
          )
        }
      </form >

      {fullscreenMode && (
        <div className="modal-footer-actions" style={{
          position: 'sticky',
          bottom: 0,
          background: 'var(--bg-light)',
          borderTop: '2px solid #e5e7eb',
          padding: '16px 32px',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          zIndex: 10,
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)'
        }}>
          <Button
            type="button"
            variant="secondary"
            onClick={handleReset}
            disabled={loading}
          >
            Reset Form
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={loading || (!isDirty && isEditing)}
            onClick={handleSubmit(onFormSubmit)}
          >
            {loading ? 'Saving...' : (isEditing ? 'Update User' : 'Create User')}
          </Button>
        </div>
      )}

      {
        isEditing && !isDirty && fullscreenMode && (
          <div style={{
            position: 'fixed',
            bottom: '80px',
            right: '32px',
            background: '#dbeafe',
            border: '1px solid var(--color-primary)',
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '14px',
            color: 'var(--color-primary)',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            zIndex: 5
          }}>
            💡 Make changes to enable the update button
          </div>
        )
      }
    </div >
  );
};

export default UserForm;
